import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CompetitionService } from '../../service/CompetitionService';
import { translatedMessage } from '../../service/LanguageService';
import { useNavigate, useParams } from 'react-router-dom';
import useNavProps from '../../hooks/useNavProps';
import SubjectListComponent from '../../components/subject/SubjectListComponent';
import SubjectProposalsListComponent from '../../components/subject/SubjectProposalsListComponent';
import SecurityService from '../../service/SecurityService';

const EvaluationSubjectList = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [competition, setCompetition] = useState(null);

    const navigate = useNavigate();
    const { setNavPropsValue } = useNavProps();
    const toast = useRef(null);
    const { competitionIdParam } = useParams();
    const competitionService = useMemo(() => new CompetitionService(), []);
    const [subjects, setSubjects] = useState([]);

    const [canViewSubjects, setCanViewSubjects] = useState(false);
    const [canEditSubjects, setCanEditSubjects] = useState(false);
    const [canEditSubjectProposals, setCanEditSubjectProposals] = useState(false);
    const [canExtractSubjects, setCanExtractSubjects] = useState(false);
    const [canInvalidateExtractedSubjects, setCanInvalidateExtractedSubjects] = useState(false);

    useEffect(() => {
        setIsLoading(true);

        const getCompetition = async () => {
            await competitionService
                .getCompetition(competitionIdParam)
                .then((competitionData) => {
                    setCompetition(competitionData);
                    setNavPropsValue('competition-evaluation', competitionData.id, competitionData.name);

                    getSubjects()
                        .then((r) => checkCanViewSubjects())
                        .then((r) => checkCanEditSubjects())
                        .then((r) => checkCanEditSubjectProposals())
                        .then((r) => checkCanExtractSubjects())
                        .then((r) => checkCanInvalidateExtractedSubjects())
                        .then((r) => setIsLoading(false));
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        getCompetition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [competitionService, competitionIdParam]);

    const checkCanViewSubjects = async () => {
        setCanViewSubjects(await SecurityService.check('CAN_VIEW_SUBJECTS', competitionIdParam));
    };
    const checkCanEditSubjects = async () => {
        setCanEditSubjects(await SecurityService.check('CAN_EDIT_SUBJECTS', competitionIdParam));
    };
    const checkCanEditSubjectProposals = async () => {
        setCanEditSubjectProposals(await SecurityService.check('CAN_EDIT_SUBJECT_PROPOSALS', competitionIdParam));
    };
    const checkCanExtractSubjects = async () => {
        setCanExtractSubjects(await SecurityService.check('CAN_EXTRACT_SUBJECT', competitionIdParam));
    };
    const checkCanInvalidateExtractedSubjects = async () => {
        setCanInvalidateExtractedSubjects(await SecurityService.check('CAN_INVALIDATE_EXTRACTED_SUBJECTS', competitionIdParam));
    };

    const getSubjects = async () => {
        competitionService
            .getCompetitionSubjects(competitionIdParam)
            .then((_subjects) => setSubjects(_subjects))
            .then((r) => checkCanViewSubjects())
            .then((r) => checkCanEditSubjects())
            .then((r) => checkCanEditSubjectProposals())
            .then((r) => checkCanExtractSubjects())
            .then((r) => checkCanInvalidateExtractedSubjects())
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <h3 className="mb-1">{competition?.name}</h3>
                            <div className="mb-3">
                                <Button className="pcn-button-slim p-button-text" onClick={() => navigate(`/competition-evaluation`)}>
                                    <div className="flex align-items-center">
                                        <FontAwesomeIcon icon="fa-solid fa-arrow-left" className="mr-1 " />
                                        <span>{translatedMessage('generic.backToList')}</span>
                                    </div>
                                </Button>
                            </div>

                            <SubjectListComponent
                                competition={competition}
                                subjects={subjects}
                                afterSave={getSubjects}
                                permissions={{
                                    canViewSubjects,
                                    canEditSubjects,
                                    canEditSubjectProposals,
                                    canExtractSubjects,
                                    canInvalidateExtractedSubjects
                                }}
                            />

                            <SubjectProposalsListComponent
                                competition={competition}
                                subjects={subjects}
                                afterSave={getSubjects}
                                permissions={{
                                    canViewSubjects,
                                    canEditSubjects,
                                    canEditSubjectProposals,
                                    canExtractSubjects,
                                    canInvalidateExtractedSubjects
                                }}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    }
};

export default EvaluationSubjectList;
